import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Meta } from '@/components/common';
import { Container } from '@/components/layout';
import { cn } from '@/lib/utils';

/**
 * The FAQ, in the design's flat one-card-per-question layout.
 *
 * Written from how the product actually behaves (updated 2026-11-03). Each
 * answer names the rule it rests on, so when one changes the answer can be
 * found: the seat_assigned email (sign-in email, password, licence key), the
 * desktop's first-run steps (activate, sign in, PIN), seats and hardware grace
 * (license_policies), the 30-day read-only window (LICENCE_READ_ONLY_PERIOD),
 * reminders at 60/30/7 days, the 7-day offline grant, Settings > Updates and
 * the release email, the review link (no account, optional passcode, 30 days),
 * the Issues thread, and the password reset code on the sign-in screen.
 * The User manual (routes/manual) goes through the same ground step by step.
 *
 * The shape: 16px radius at 22px/26px, the question at 16px weight 700, a
 * chevron that rotates, the answer at 14px/1.7 capped at 70ch, cards 12px
 * apart, and the open card's border darkening to #CBD5E1.
 */
const FAQS = [
  {
    q: 'I have received my welcome email. What do I do now?',
    a: 'The email "Your NμDent licence key and sign-in details" has your sign-in email, a password and your licence key. Sign in to this portal with the email and password, then change the password under Profile > Change password. Download NμDent from the Dashboard, install it, and on first launch enter your email and licence key, sign in with your portal password and choose a 4-digit PIN. The User manual walks through each step.',
  },
  {
    q: 'Where do I download NμDent?',
    a: 'On the Dashboard, beside Software version. Download gives the Windows setup; "Also for Linux" appears when there is a Linux build. The Windows setup is small and fetches the rest of NμDent while it installs, so keep the computer online until it finishes.',
  },
  {
    q: 'How many computers can one licence run on?',
    a: 'A Single Seat licence runs on one computer. A Lab Seat licence has one seat per computer - five unless your lab bought more. Each seat belongs to the computer it was first activated on.',
  },
  {
    q: 'I replaced or reinstalled a computer. How do I move my seat?',
    a: 'Raise an issue on the Issues page under Licensing and say which computer it was. Support releases the seat from the old machine and you get an email saying your licence has been reset. Then activate NμDent on the new computer with the same email and licence key. If NμDent notices changed hardware on its own, a Single Seat keeps working for 14 days while that is sorted out; a Lab Seat stops until support releases it.',
  },
  {
    q: 'What happens when my licence expires?',
    a: 'You get a reminder email 60, 30 and 7 days before, as long as "Licence expiry reminders" is on in Settings. For 30 days after the expiry date NμDent still opens and your cases stay viewable, but the NμCAD workbench does not start. After those 30 days NμDent will not open until the licence is renewed. Raise an issue under Licensing to renew.',
  },
  {
    q: 'Can I use NμDent without an internet connection?',
    a: 'Yes, for up to 7 days after it last reached our servers. When there is no connection, NμDent opens with the 4-digit PIN you chose on first launch. Settings > Profile in NμDent shows how many days are left; connect again before it runs out and NμDent renews it on its own.',
  },
  {
    q: 'How do I update NμDent?',
    a: 'NμDent checks for a newer version when it starts, and Settings > Updates shows what you have installed and anything newer, with what changed. Some updates are marked as required and must be installed to keep working. With "Version update available" on in Settings, we also email you when a new version is out.',
  },
  {
    q: 'Which scan files can I use, and what can I export?',
    a: 'Scans come in as STL, PLY or OBJ, from any intraoral scanner. A finished crown exports as STL for your mill or printer, or as a 3D PDF with a section for the doctor.',
  },
  {
    q: 'How does a dentist review a crown design?',
    a: 'Send it for review from NμCAD. The dentist gets a link by email - no account and nothing to install - where they turn the crown around, leave notes on the model, and approve it, ask for changes or reject it. You can add a passcode to the link, and it stays valid for 30 days. The decision and notes come back to NμDent, and the Review Links page shows where each one stands.',
  },
  {
    q: 'I forgot my password.',
    a: 'Choose "Reset password" beside the password field on the sign-in screen and enter your email. We email you a 6-digit code; type it back on the same screen and choose a new password. The same password works in this portal and in NμDent.',
  },
  {
    q: 'How do I get help, and where do I see the answer?',
    a: 'Raise an issue on the Issues page, pick the category that fits and describe what happened. Support replies on the issue itself and by email. You can reply on the issue too - if one marked resolved is still happening, reply there and it goes back to support. If you cannot sign in at all, email helpdesk.nuvoai@gmail.com or call 1800-202-7201.',
  },
] as const;

function FaqCard({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={cn(
        'rounded-2xl border bg-card shadow-[0_1px_3px_rgba(15,23,42,0.05)] transition-colors',
        open ? 'border-[#CBD5E1]' : 'border-[#E3E7ED]',
      )}
    >
      {/*
        A button rather than <details>, so the state is React's and the whole
        row is a proper control for a screen reader and for the keyboard.
      */}
      <button
        type="button"
        onClick={() => {
          setOpen((was) => !was);
        }}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-[26px] py-[22px] text-left"
      >
        <span className="text-base leading-[1.4] font-bold">{q}</span>
        <ChevronDown
          size={17}
          strokeWidth={2.2}
          className={cn(
            'flex-none text-muted-foreground transition-transform duration-[180ms]',
            open && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {open && (
        <p className="max-w-[70ch] px-[26px] pb-[22px] text-sm leading-[1.7] text-pretty text-[#5B6B87]">
          {a}
        </p>
      )}
    </div>
  );
}

export function FaqPage() {
  return (
    <>
      <Meta title="FAQ" />

      <Container className="flex flex-col gap-3">
        {FAQS.map((item) => (
          <FaqCard key={item.q} q={item.q} a={item.a} />
        ))}
      </Container>
    </>
  );
}
